import { logger } from "@/utils/logger";

import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo, useRef } from 'react';
import { FormType } from '../hooks/useChat';
import { FormFrameworkRef } from '../components/FormFramework';
import { ga4Tracker, extractVehicleData } from '../utils/ga4Tracking';
import { useStateManager } from '../hooks/useStateManager';
import type { DepartmentHours } from '@/utils/messageParser/types';

// Extend FormType to include Carfax, FinanceV1, and TradeV2
type ExtendedFormType = FormType | 'Carfax' | 'FinanceV1' | 'TradeV2';

interface FormContextType {
  activeForm: ExtendedFormType | null;
  isOfferHighlighted: boolean;
  formData: { [key: string]: string };
  aiFormData: { [key: string]: any } | null; // NEW: Store AI Form object data
  setActiveForm: (form: ExtendedFormType | null, aiData?: { [key: string]: any }) => void;
  setIsOfferHighlighted: (highlighted: boolean) => void;
  setFormData: (data: { [key: string]: string }) => void;
  updateFormData: (data: Partial<{ [key: string]: string }>) => void;
  activateOfferForm: () => void;
  activateCarfaxForm: (vin?: string) => void;
  closeForm: (force?: boolean) => void;
  carfaxVin: string | undefined;
  formRef: React.RefObject<FormFrameworkRef> | null;
  setFormRef: (ref: React.RefObject<FormFrameworkRef>) => void;
  openingHours: DepartmentHours[] | undefined;
  setOpeningHours: (hours: DepartmentHours[] | undefined) => void;
  isUserInteracting: boolean;
  setUserInteracting: (interacting: boolean) => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

export const FormProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { state, updateFormState, isRestoring } = useStateManager();
  
  // Get form state from state manager
  const activeForm = state.activeForm as ExtendedFormType | null;
  const isOfferHighlighted = state.isOfferHighlighted;
  const formData = state.formData;
  const aiFormData = state.aiFormData;
  
  // Local form state not persisted
  const [carfaxVin, setCarfaxVin] = useState<string | undefined>(undefined);
  const [formRef, setFormRef] = useState<React.RefObject<FormFrameworkRef> | null>(null);
  const [openingHours, setOpeningHours] = useState<DepartmentHours[] | undefined>(undefined);
  const [isUserInteracting, setIsUserInteracting] = useState(false);

  // FormProvider subscribes to the global store, so it re-renders on every state
  // write anywhere in the widget. Every callback below is therefore memoized and the
  // context `value` is memoized too — as bare functions in a fresh object they handed
  // a new identity to every consumer on each write, which permanently churned the
  // dependency arrays of consumers that list them (ChatLayout*, useFormAutoClose,
  // ServiceFormMessage) and turned ordinary effects into unbounded render loops.
  //
  // `formRef` and `isUserInteracting` are mirrored into refs so the callbacks can stay
  // dependency-free without capturing stale values (the interaction guard in
  // `closeForm` in particular must always see the live value).
  const formRefRef = useRef(formRef);
  formRefRef.current = formRef;
  const isUserInteractingRef = useRef(isUserInteracting);
  isUserInteractingRef.current = isUserInteracting;

  // Update functions for state manager
  const updateFormDataLocal = useCallback((data: Partial<{ [key: string]: string }>) => {
    updateFormState(prev => ({
      formData: { ...(prev.formData || {}), ...data },
    }));
  }, [updateFormState]);

  const setFormDataLocal = useCallback((data: { [key: string]: string }) => {
    updateFormState({ formData: data });
  }, [updateFormState]);

  const setIsOfferHighlightedLocal = useCallback((highlighted: boolean) => {
    updateFormState({ isOfferHighlighted: highlighted });
  }, [updateFormState]);

  const handleSetOpeningHours = useCallback((hours: DepartmentHours[] | undefined) => {
    setOpeningHours(hours);
  }, []);

  const setActiveForm = useCallback((form: ExtendedFormType | null, aiData?: { [key: string]: any }) => {
    updateFormState({ 
      activeForm: form,
      aiFormData: aiData || null
    });
    
    // Set highlighting for Offer form when it comes from "Offers" type
    if (form === 'Offer' && aiData?.Type === 'Offers') {
      updateFormState({ isOfferHighlighted: true });
      // Track GA4 form start event
      ga4Tracker.trackFormStart('offer');
    } else if (form === 'Offer' && aiData?.Type === 'Offer') {
      updateFormState({ isOfferHighlighted: false });
    }
    
    // Auto-expand form whenever a form is activated
    if (form) {
      setTimeout(() => {
        if (formRefRef.current?.current) {
          formRefRef.current.current.expand();
        }
      }, 100);
    }
  }, [updateFormState]);

  const activateOfferForm = useCallback(() => {
    updateFormState({
      activeForm: 'Offer',
      isOfferHighlighted: true,
      formData: {}
    });

    // Track GA4 ASC events for offer
    ga4Tracker.trackFormStart('offer');

    // Expand form if it's collapsed
    if (formRefRef.current?.current) {
      formRefRef.current.current.expand();
    }
  }, [updateFormState]);

  const activateCarfaxForm = useCallback((vin?: string) => {
    setCarfaxVin(vin);
    updateFormState({
      activeForm: 'Carfax',
      isOfferHighlighted: false
    });

    // Track GA4 ASC events for CARFAX
    ga4Tracker.trackFormStart('carfax');

    // Expand form if it's collapsed
    if (formRefRef.current?.current) {
      formRefRef.current.current.expand();
    }
  }, [updateFormState]);

  const closeForm = useCallback((force: boolean = false) => {
    // Check if any input/textarea/select in the form currently has focus
    const activeElement = document.activeElement;
    const isInputFocused = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT'
    );
    
    // Prevent auto-closing if user is actively interacting OR has an input focused (unless forced)
    if (!force && (isUserInteractingRef.current || isInputFocused)) {
      logger.log('🔒 FORM LOCKED: User is interacting or input focused, preventing auto-close', {
        isUserInteracting: isUserInteractingRef.current,
        isInputFocused,
        activeElementTag: activeElement?.tagName
      });
      return;
    }

    updateFormState({
      activeForm: null,
      isOfferHighlighted: false,
      formData: {}
    });

    setIsUserInteracting(false); // Reset interaction state when closing

    // Set timestamp in localStorage to prevent immediate form reactivation
    localStorage.setItem('lastFormCloseTime', Date.now().toString());
  }, [updateFormState]);

  const value: FormContextType = useMemo(() => ({
    activeForm,
    isOfferHighlighted,
    formData,
    aiFormData,
    setActiveForm,
    setIsOfferHighlighted: setIsOfferHighlightedLocal,
    setFormData: setFormDataLocal,
    updateFormData: updateFormDataLocal,
    activateOfferForm,
    activateCarfaxForm,
    closeForm,
    carfaxVin,
    formRef,
    setFormRef,
    openingHours,
    setOpeningHours: handleSetOpeningHours,
    isUserInteracting,
    setUserInteracting: setIsUserInteracting,
  }), [
    activeForm, isOfferHighlighted, formData, aiFormData,
    setActiveForm, setIsOfferHighlightedLocal, setFormDataLocal, updateFormDataLocal,
    activateOfferForm, activateCarfaxForm, closeForm,
    carfaxVin, formRef, openingHours, handleSetOpeningHours, isUserInteracting,
  ]);

  return (
    <FormContext.Provider value={value}>
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};
